/**
 * `/tds` — TDS / Form 26Q preparation (S33-sibling).
 *
 * V1.0 ships a read-only surface: per-section roll-up of TDS deducted in
 * the current quarter, plus a recent-deductions table and a Form-16A
 * roster. system-decisions §9: Flint prepares, CA files.
 *
 * Chrome mirrors `prototype/compliance/tds.html` — TAN profile strip + quarter picker
 * + 4-tile KPI strip + section bars + deductions table + Form-16A queue
 * + deposit timeline + LDC roster. V1.x parts (challan 281 / TRACES sync /
 * Form-16A PDF / LDC roster) render as disabled with a "Lands in V1.x"
 * tooltip — same honesty seam as `/gst-returns` `unsupportedCodes`.
 *
 * @see prototype/compliance/tds.html — V1.1 design baseline
 * @see docs/spec/08-system-decisions.md §9 — compliance posture
 */
import { useMemo, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
  Award,
  Calendar,
  ChevronRight,
  Clock3,
  Download,
  FileBarChart,
  FileText,
  Info,
  PieChart,
  Send,
  ShieldCheck,
  TrendingUp,
} from "lucide-react";
import { Alert } from "@flint/ui";
import type { tds as tdsContract } from "@flint/api-contract";
import { trpc } from "../../../../lib/trpc.ts";
import { WorkspaceShell } from "../../../../features/workspace/index.ts";
import { formatDate } from "../../../../lib/format.ts";

export const Route = createFileRoute("/(workspace)/compliance/tds/")({
  component: TdsPage,
});

interface QuarterValue {
  fy: number;
  quarter: 1 | 2 | 3 | 4;
}

function TdsPage() {
  // Default to the current FY-quarter. URL-search-params upgrade is V1.0.x.
  const [active, setActive] = useState<QuarterValue>(() => currentFyQuarter());

  const summary = trpc.tds.getQuarterSummary.useQuery({
    fy: active.fy,
    quarter: active.quarter,
  });
  const data = summary.data;
  const unsupported = useMemo(
    () => new Set(data?.unsupportedFeatures ?? []),
    [data?.unsupportedFeatures],
  );

  // 8-quarter strip — 7 back + 1 forward.
  const quarters = useMemo(() => buildQuarterStrip(active), [active]);

  return (
    <WorkspaceShell breadcrumbs={[{ label: "Compliance" }, { label: "TDS · Form 26Q" }]}>
      <div className="flex flex-col gap-[18px]">
        {/* Page head */}
        <header className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <h1 className="text-2xl font-bold tracking-tight text-ink leading-tight">
              TDS · Form 26Q
            </h1>
            <p className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-muted">
              <span>
                <strong className="text-ink">{quarterLabel(active)}</strong>
              </span>
              <Sep />
              {data ? (
                <>
                  <span>
                    <span className="num text-ink font-semibold">{data.totals.paymentCount}</span>{" "}
                    {data.totals.paymentCount === 1 ? "deduction" : "deductions"} ·{" "}
                    <span className="num text-ink font-semibold">{data.totals.vendorCount}</span>{" "}
                    {data.totals.vendorCount === 1 ? "vendor" : "vendors"}
                  </span>
                  <Sep />
                  <span>
                    26Q due{" "}
                    <strong className="text-rose-700">{formatDate(data.return26qDueOn)}</strong>
                    {" · "}
                    <DaysToDue dueOn={data.return26qDueOn} />
                  </span>
                </>
              ) : null}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0 flex-wrap">
            {/* "Export 26Q file" — V1.x. The RPU-format export the CA hands
                to TRACES. */}
            <DisabledButton tooltip="26Q RPU export lands in V1.x" tone="secondary">
              <Download size={13} strokeWidth={2} />
              Export 26Q file
            </DisabledButton>
            {/* "Form 16A queue · N" — functional, scrolls to the roster. */}
            <a
              href="#tds-form16a"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-xs font-semibold bg-paper text-ink-2 border border-line hover:bg-bg hover:border-line-2 transition-colors no-underline"
            >
              <FileText size={13} strokeWidth={2} />
              Form 16A queue
              {data ? (
                <span className="px-1.5 py-px rounded-full bg-bg text-xs font-bold text-ink-2 ml-0.5">
                  {data.form16aRoster.length}
                </span>
              ) : null}
            </a>
            {/* "Record payment" — functional, links to /payments where the
                vendor payment (incl. TDS deduction) is recorded. */}
            <Link
              to="/procurement/payments"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-xs font-semibold bg-paper text-ink-2 border border-line hover:bg-bg hover:border-line-2 transition-colors no-underline"
            >
              <Send size={13} strokeWidth={2} />
              Record payment
            </Link>
            {/* "Generate challan 281" — V1.x. The ITNS-281 challan that
                deposits the deducted TDS with the government. */}
            <DisabledButton
              tooltip="Challan 281 generation lands in V1.x"
              tone="primary"
              shown={unsupported.has("generate-challan-281")}
            >
              Generate challan 281
            </DisabledButton>
          </div>
        </header>

        {/* TAN profile strip — mirrors `prototype/compliance/tds.html:601-618`. The
            two right-side mini buttons (View on TRACES / RPU export) and
            the "Liable since" / "TRACES synced" meta items are V1.x —
            they render as muted placeholders so the chrome matches and
            the user sees where the data will land. */}
        {data ? (
          <div className="flex items-center gap-4 px-4 py-3 bg-paper border border-line rounded-md shadow-sm flex-wrap">
            <span className="inline-grid place-items-center w-9 h-9 rounded-sm bg-slate-100 text-slate-700 flex-shrink-0">
              <ShieldCheck size={16} strokeWidth={2} />
            </span>
            <div className="flex gap-3.5 flex-wrap items-center text-xs text-muted flex-1 min-w-0">
              <strong className="text-ink font-semibold">{data.company.legalName}</strong>
              <span className="flex items-center gap-1.5">
                TAN
                <span
                  className="font-mono text-muted font-medium px-2 py-0.5 bg-bg rounded-sm border border-dashed border-line-2"
                  title={data.company.tan ? undefined : "Set in Settings · V1.x"}
                >
                  {data.company.tan ?? "— · V1.x"}
                </span>
              </span>
              {data.company.pan ? (
                <span className="flex items-center gap-1.5">
                  PAN
                  <span className="font-mono text-ink-2 font-semibold px-2 py-0.5 bg-bg rounded-sm">
                    {data.company.pan}
                  </span>
                </span>
              ) : null}
              <span className="inline-flex px-2 py-0.5 bg-emerald-50 text-emerald-700 rounded-sm text-xs font-semibold uppercase tracking-wide">
                Deductor · Company
              </span>
              {/* "Liable since" — V1.x; lands when the onboarding "TDS
                  liability since FY" field ships. */}
              <span className="inline-flex items-center gap-1.5 text-muted">
                Liable since
                <span
                  className="text-muted font-medium px-1.5 py-0.5 bg-bg rounded-sm border border-dashed border-line-2 text-xs"
                  title="Lands with onboarding TDS-liability field · V1.x"
                >
                  FY — · V1.x
                </span>
              </span>
              {/* "TRACES synced" — V1.x; lands with TRACES adapter. */}
              <span className="inline-flex items-center gap-1.5 text-muted">
                TRACES synced
                <span
                  className="text-muted font-medium px-1.5 py-0.5 bg-bg rounded-sm border border-dashed border-line-2 text-xs"
                  title="TRACES sync lands in V1.x"
                >
                  — · V1.x
                </span>
              </span>
            </div>
            <div className="inline-flex gap-1.5 flex-shrink-0">
              <DisabledButton
                tooltip="TRACES portal sync lands in V1.x"
                tone="mini"
                shown={unsupported.has("view-on-traces")}
              >
                View on TRACES
              </DisabledButton>
              <DisabledButton
                tooltip="RPU export lands in V1.x"
                tone="mini"
                shown={unsupported.has("rpu-export")}
              >
                RPU export
              </DisabledButton>
            </div>
          </div>
        ) : null}

        {/* Quarter strip */}
        <div className="flex items-center gap-3 flex-wrap">
          <div
            className="inline-flex bg-paper border border-line rounded-sm p-[3px] gap-[1px] flex-wrap"
            role="tablist"
            aria-label="TDS quarter"
          >
            {quarters.map((q) => {
              const isOn = q.fy === active.fy && q.quarter === active.quarter;
              return (
                <button
                  key={q.key}
                  type="button"
                  role="tab"
                  aria-selected={isOn}
                  onClick={() => setActive({ fy: q.fy, quarter: q.quarter })}
                  className={`px-2.5 py-1.5 rounded-sm text-xs font-semibold flex flex-col items-center gap-[1px] leading-[1.1] min-w-[88px] ${
                    isOn
                      ? "bg-sky-50 text-sky-700"
                      : "text-muted hover:bg-bg hover:text-ink-2"
                  }`}
                >
                  <span className="flex items-center gap-1">
                    <span
                      className={`inline-block w-[6px] h-[6px] rounded-full ${q.dotClass(isOn)}`}
                      aria-hidden
                    />
                    {q.label}
                  </span>
                  <span
                    className={`text-xs uppercase tracking-wide ${isOn ? "text-sky-600" : "text-muted"} font-medium`}
                  >
                    {q.status}
                  </span>
                </button>
              );
            })}
          </div>
          {data ? (
            <span className="text-xs text-muted inline-flex items-center gap-1.5 flex-wrap">
              <Calendar size={12} strokeWidth={2} />
              {formatDate(data.periodStart)} → {formatDate(data.periodEnd)} · 26Q due{" "}
              <span className="text-rose-700 font-bold">{formatDate(data.return26qDueOn)}</span> ·
              Form 16A by{" "}
              <span className="text-ink font-semibold">{formatDate(data.form16aDueOn)}</span>
            </span>
          ) : null}
        </div>

        {/* KPI strip */}
        {data ? (
          <section
            className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3"
            aria-label="TDS summary"
          >
            <KpiTile
              accent="indigo"
              label="TDS deducted · quarter"
              value={formatRupeesShort(BigInt(data.totals.quarterTdsPaise))}
              meta={`${data.totals.vendorCount} ${data.totals.vendorCount === 1 ? "vendor" : "vendors"} · ${data.breakdown.length} sections · across ${data.totals.paymentCount} payments`}
              icon={<ShieldCheck size={13} strokeWidth={2} />}
            />
            <KpiTile
              accent="rose"
              label="Pending deposit"
              value="—"
              meta={
                <span className="inline-flex items-center gap-1.5">
                  <span className="px-1.5 py-px rounded-sm bg-bg text-xs font-bold uppercase tracking-wider text-muted">
                    V1.x
                  </span>
                  ITNS-281 challan timeline
                </span>
              }
              icon={<Clock3 size={13} strokeWidth={2} />}
              muted
            />
            <KpiTile
              accent="emerald"
              label={`TDS this FY · ${fyLabel(active.fy)}`}
              value={formatRupeesShort(BigInt(data.totals.fyTdsPaise))}
              meta="Includes the selected quarter"
              icon={<TrendingUp size={13} strokeWidth={2} />}
            />
            <KpiTile
              accent="emerald"
              label="On-time score · 26Q"
              value="—"
              meta={
                <span className="inline-flex items-center gap-1.5">
                  <span className="px-1.5 py-px rounded-sm bg-bg text-xs font-bold uppercase tracking-wider text-muted">
                    V1.x
                  </span>
                  Tracks once challans deposit
                </span>
              }
              icon={<Award size={13} strokeWidth={2} />}
              muted
            />
          </section>
        ) : null}

        {/* Coverage banner */}
        {data ? (
          <div className="flex gap-2.5 px-3.5 py-2.5 bg-sky-50 border border-sky-100 border-l-[3px] border-l-sky-600 rounded-sm text-xs text-sky-700 items-start">
            <Info size={14} strokeWidth={2} className="flex-shrink-0 mt-[1px]" />
            <span>
              <strong className="font-semibold">
                V1 covers TDS sections 194C, 194J, 194Q, 194I, 194H.
              </strong>{" "}
              Section rates are computed at payment time from the vendor's section snapshot — see
              Bills → Record payment for the per-payment TDS detail. Challan generation, TRACES
              upload, and Form 16A PDFs land in V1.x; for now hand the export to your CA.
            </span>
          </div>
        ) : null}

        {/* Section breakdown */}
        {summary.isLoading ? (
          <p className="text-sm text-muted">Loading TDS quarter summary…</p>
        ) : summary.error ? (
          <Alert intent="bad" title="Couldn't load summary" description={summary.error.message} />
        ) : data ? (
          <>
            <SectionBreakdown data={data} />
            <RecentDeductions data={data} />
            <Form16ARoster data={data} unsupported={unsupported} />
            <DepositTimeline data={data} />
            <LdcRoster />
          </>
        ) : null}
      </div>
    </WorkspaceShell>
  );
}

// ── Section breakdown ─────────────────────────────────────────────

/**
 * Section breakdown — 2-col grid mirroring `prototype/compliance/tds.html:692-786`.
 * Left: donut visualization (V1.x — disabled with a legend showing the
 * real section totals). Right: section bars (real, filterable in V1.x).
 *
 * The legend's amounts are real — only the visual donut graph itself is
 * V1.x. That keeps the actual figures honest while signalling the chart
 * itself isn't drawn yet.
 */
function SectionBreakdown({ data }: { data: TdsSummaryData }) {
  const top = data.breakdown[0];
  const maxPaise = top ? BigInt(top.tdsPaise) : 0n;
  return (
    <>
      {/* Section header — prototype's `section-h` block (line 687) */}
      <header className="flex items-end justify-between gap-3 flex-wrap">
        <h2 className="text-base font-bold tracking-tight text-ink inline-flex items-baseline gap-2 m-0">
          Section breakdown
          <span className="text-xs text-muted font-medium">
            · {data.breakdown.length} {data.breakdown.length === 1 ? "section" : "sections"}
          </span>
        </h2>
        <span className="text-xs text-muted">
          Quarter ·{" "}
          <strong className="text-ink-2 font-semibold">
            {formatRupees(BigInt(data.totals.quarterTdsPaise))}
          </strong>
        </span>
      </header>
      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,360px)_minmax(0,1fr)] gap-3">
        {/* Donut panel — V1.x. Legend amounts are real. */}
        <section
          className="bg-paper border border-line rounded-md shadow-sm"
          aria-label="TDS by section donut"
        >
          <header className="flex items-center justify-between px-4 py-2.5 border-b border-line">
            <span className="text-sm font-semibold text-ink inline-flex items-center gap-1.5">
              <PieChart size={14} strokeWidth={2} />
              By section
            </span>
            <span className="text-xs text-muted">
              Quarter · {formatRupeesShort(BigInt(data.totals.quarterTdsPaise))}
            </span>
          </header>
          <div className="p-4 flex flex-col gap-3 items-center">
            {/* Disabled donut placeholder — a circular outline with the
                quarter total in the middle. V1.x replaces with an SVG arc
                visualization keyed off `data.breakdown`. */}
            <div className="relative">
              <div className="w-[148px] h-[148px] rounded-full border-[14px] border-dashed border-line-2 grid place-items-center text-center">
                <div>
                  <div className="num text-xl font-bold tracking-tight text-ink leading-none">
                    {formatRupeesShort(BigInt(data.totals.quarterTdsPaise))}
                  </div>
                  <div className="text-xs uppercase tracking-wider text-muted mt-1">
                    Quarter total
                  </div>
                </div>
              </div>
              <span className="absolute -top-1 -right-1 px-1.5 py-px rounded-sm bg-paper text-xs font-bold uppercase tracking-wider text-muted border border-line">
                Chart · V1.x
              </span>
            </div>
            {/* Legend — uses real per-section paise. */}
            <div className="w-full flex flex-col gap-1.5 text-xs">
              {data.breakdown.length === 0 ? (
                <p className="text-muted text-center m-0">No sections this quarter.</p>
              ) : (
                data.breakdown.map((b) => {
                  const accent = sectionAccent(b.code);
                  return (
                    <span key={b.code} className="inline-flex items-center gap-2">
                      <span
                        className={`inline-block w-2.5 h-2.5 rounded-sm ${accent.bar}`}
                        aria-hidden
                      />
                      <span className="text-ink-2">
                        {b.code} · {legendShort(b.label)}
                      </span>
                      <span className="num ml-auto text-ink font-bold">
                        {formatRupeesShort(BigInt(b.tdsPaise))}
                      </span>
                    </span>
                  );
                })
              )}
            </div>
            {data.breakdown.length > 0 ? (
              <p className="text-xs text-muted leading-relaxed m-0">
                <strong className="text-ink">Top section · {data.breakdown[0]?.code}</strong>{" "}
                accounted for {formatRupees(BigInt(data.breakdown[0]?.tdsPaise ?? "0"))} this
                quarter ({data.breakdown[0]?.vendorCount}{" "}
                {data.breakdown[0]?.vendorCount === 1 ? "vendor" : "vendors"}).
              </p>
            ) : null}
          </div>
        </section>

        {/* Section bars panel — real */}
        <section
          className="bg-paper border border-line rounded-md shadow-sm"
          aria-label="Section-wise detail"
        >
          <header className="flex items-center justify-between px-4 py-2.5 border-b border-line">
            <span className="text-sm font-semibold text-ink inline-flex items-center gap-1.5">
              <FileBarChart size={14} strokeWidth={2} />
              Section-wise detail
            </span>
            <span className="text-xs text-muted">Filter the deductions table (V1.x)</span>
          </header>
          <div className="p-4 flex flex-col gap-2.5">
            {data.breakdown.length === 0 ? (
              <p className="text-sm text-muted">No TDS deductions in this quarter.</p>
            ) : (
              data.breakdown.map((b) => <SectionBar key={b.code} row={b} maxPaise={maxPaise} />)
            )}
          </div>
        </section>
      </div>
    </>
  );
}

/** Shorten a section label for the donut legend. */
function legendShort(label: string): string {
  return label
    .toLowerCase()
    .split(/[\s/&]+/)
    .slice(0, 2)
    .join(" ");
}

interface SectionBarProps {
  row: {
    code: string;
    label: string;
    hint: string;
    vendorCount: number;
    paymentCount: number;
    tdsPaise: string;
    grossPaise: string;
  };
  maxPaise: bigint;
}

function SectionBar({ row, maxPaise }: SectionBarProps) {
  const paise = BigInt(row.tdsPaise);
  const widthPct = maxPaise === 0n ? 0 : Number((paise * 1000n) / maxPaise) / 10;
  const accent = sectionAccent(row.code);
  return (
    <div className="border border-line rounded-md px-3.5 py-2.5 hover:border-line-2 transition-colors">
      <div className="flex items-center justify-between gap-2 mb-1.5">
        <div className="inline-flex items-center gap-2 min-w-0">
          <span
            className={`inline-flex items-center justify-center px-1.5 py-0.5 rounded-sm text-xs font-bold font-mono ${accent.chip}`}
          >
            {row.code}
          </span>
          <strong className="text-sm text-ink font-semibold truncate">{row.label}</strong>
        </div>
        <span className="num text-sm text-ink font-bold flex-shrink-0">{formatRupees(paise)}</span>
      </div>
      <div className="w-full h-[6px] bg-bg rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${accent.bar}`} style={{ width: `${widthPct}%` }} />
      </div>
      <div className="flex items-center justify-between gap-2 mt-1.5 text-xs text-muted">
        <span>
          <strong className="text-ink-2 font-semibold">
            {row.vendorCount} {row.vendorCount === 1 ? "vendor" : "vendors"}
          </strong>
          {" · "}
          {row.paymentCount} {row.paymentCount === 1 ? "payment" : "payments"} · gross{" "}
          <span className="num">{formatRupeesShort(BigInt(row.grossPaise))}</span>
        </span>
        <span className="text-muted">{row.hint}</span>
      </div>
    </div>
  );
}

// ── Recent deductions table ──────────────────────────────────────

function RecentDeductions({ data }: { data: TdsSummaryData }) {
  if (data.recentDeductions.length === 0) {
    return null;
  }
  return (
    <section
      className="bg-paper border border-line rounded-md shadow-sm overflow-hidden"
      aria-label="Recent deductions"
    >
      <header className="flex items-center justify-between px-4 py-2.5 border-b border-line">
        <span className="text-sm font-semibold text-ink">
          Recent deductions · {data.recentDeductions.length} of {data.totals.paymentCount}
        </span>
        <Link
          to="/procurement/payments"
          className="text-xs text-sky-700 font-semibold hover:underline inline-flex items-center gap-0.5"
        >
          All payments <ChevronRight size={12} strokeWidth={2.4} />
        </Link>
      </header>
      <table className="w-full text-sm">
        <thead className="bg-bg">
          <tr className="text-left text-xs font-semibold uppercase tracking-wider text-muted border-b border-line">
            <th className="px-3 py-2.5 font-semibold w-[80px]">Section</th>
            <th className="px-3 py-2.5 font-semibold">Vendor</th>
            <th className="px-3 py-2.5 font-semibold w-[100px]">Paid on</th>
            <th className="px-3 py-2.5 font-semibold text-right">Gross</th>
            <th className="px-3 py-2.5 font-semibold text-right">TDS</th>
            <th className="px-3 py-2.5 font-semibold w-[80px]">Mode</th>
          </tr>
        </thead>
        <tbody>
          {data.recentDeductions.map((d) => {
            const code = d.tdsSection ?? "other";
            const accent = sectionAccent(code);
            return (
              <tr key={d.id} className="border-b border-line last:border-b-0 hover:bg-bg">
                <td className="px-3 py-2.5 align-middle">
                  <span
                    className={`inline-flex items-center justify-center px-1.5 py-0.5 rounded-sm font-mono text-xs font-bold ${accent.chip}`}
                  >
                    {d.tdsSection ?? "—"}
                  </span>
                </td>
                <td className="px-3 py-2.5 align-middle">
                  <Link
                    to="/procurement/vendors/$vendorId"
                    params={{ vendorId: d.vendorId }}
                    className="text-ink font-semibold hover:text-sky-700"
                  >
                    {d.vendorName}
                  </Link>
                  {d.reference ? (
                    <div className="text-xs text-muted font-mono mt-px truncate max-w-[300px]">
                      {d.reference}
                    </div>
                  ) : null}
                </td>
                <td className="px-3 py-2.5 align-middle num text-xs text-muted">
                  {formatDate(d.paidOn)}
                </td>
                <td className="px-3 py-2.5 align-middle text-right num">
                  {formatRupees(BigInt(d.grossPaise))}
                </td>
                <td className="px-3 py-2.5 align-middle text-right num text-ink font-bold">
                  {formatRupees(BigInt(d.tdsPaise))}
                  {d.tdsRateBp !== null ? (
                    <div className="text-xs text-muted font-normal mt-px">
                      {(d.tdsRateBp / 100).toFixed(d.tdsRateBp % 100 === 0 ? 0 : 1)}%
                    </div>
                  ) : null}
                </td>
                <td className="px-3 py-2.5 align-middle text-xs text-muted uppercase tracking-wide">
                  {d.mode}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </section>
  );
}

// ── Form 16A roster ──────────────────────────────────────────────

function Form16ARoster({ data, unsupported }: { data: TdsSummaryData; unsupported: Set<string> }) {
  if (data.form16aRoster.length === 0) return null;
  const pdfDisabled = unsupported.has("form-16a-pdf");
  return (
    <section
      id="tds-form16a"
      className="bg-paper border border-line rounded-md shadow-sm scroll-mt-4"
      aria-label="Form 16A roster"
    >
      <header className="flex items-center justify-between px-4 py-2.5 border-b border-line">
        <span className="text-sm font-semibold text-ink">
          Form 16A queue · {data.form16aRoster.length}{" "}
          {data.form16aRoster.length === 1 ? "vendor" : "vendors"}
        </span>
        <span className="text-xs text-muted">
          Issue within 15 days of 26Q filing · by{" "}
          <strong className="text-ink font-semibold">{formatDate(data.form16aDueOn)}</strong>
        </span>
      </header>
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-2.5 p-4">
        {data.form16aRoster.map((v) => (
          <div
            key={v.vendorId}
            className="border border-line rounded-md p-3 flex flex-col gap-2 hover:border-line-2 transition-colors"
          >
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-sm bg-slate-700 text-paper grid place-items-center text-xs font-bold flex-shrink-0">
                {avatarInitials(v.vendorName)}
              </div>
              <div className="flex-1 min-w-0">
                <Link
                  to="/procurement/vendors/$vendorId"
                  params={{ vendorId: v.vendorId }}
                  className="text-sm text-ink font-semibold hover:text-sky-700 block truncate"
                >
                  {v.vendorName}
                </Link>
                <div className="text-xs text-muted">
                  {v.sections.length === 0
                    ? "Section —"
                    : v.sections.map((s) => `Section ${s}`).join(" · ")}
                </div>
              </div>
            </div>
            <div className="flex items-center justify-between text-xs gap-2">
              <div>
                <div className="text-muted">Total TDS · quarter</div>
                <div className="num text-base font-bold text-ink mt-0.5">
                  {formatRupees(BigInt(v.tdsPaise))}
                </div>
              </div>
              <div>
                <div className="text-muted text-right">Payments</div>
                <div className="num text-base font-bold text-ink-2 mt-0.5 text-right">
                  {v.paymentCount}
                </div>
              </div>
            </div>
            <DisabledButton
              tone="secondary"
              tooltip="Form 16A PDF generation lands in V1.x"
              shown={pdfDisabled}
              full
            >
              <Download size={12} strokeWidth={2} />
              Send PDF
            </DisabledButton>
          </div>
        ))}
      </div>
    </section>
  );
}

// ── Deposit timeline ──────────────────────────────────────────────

/**
 * Monthly-deposit timeline — mirrors `prototype/compliance/tds.html:881-949`. The
 * timeline shows when each month's deducted TDS was/needs-to-be deposited
 * via challan ITNS-281 (due by the 7th of the following month).
 *
 * V1.0 ships the deduction side (`vendor_payments.tds_paise`), but not
 * yet the deposit-and-challan side — that lands once the `cash_movements`
 * table arrives. We render the chrome anyway, with rows pre-populated
 * from the quarter's three months, all marked "Pending · V1.x" so the
 * user sees the workflow that will land.
 */
function DepositTimeline({ data }: { data: TdsSummaryData }) {
  // Generate three deposit-due rows (one per quarter month) + a Form-26Q
  // filing-due row at the quarter end. Same data points the prototype
  // shows, just disabled.
  const events = useMemo(() => buildTimelineEvents(data), [data]);
  return (
    <section
      className="bg-paper border border-line rounded-md shadow-sm"
      aria-label="Deposit timeline"
    >
      <header className="flex items-center justify-between px-4 py-2.5 border-b border-line">
        <span className="text-sm font-semibold text-ink inline-flex items-center gap-2">
          Monthly deposit timeline · ITNS-281
          <span className="px-1.5 py-px rounded-sm bg-bg text-xs font-bold uppercase tracking-wider text-muted border border-line">
            V1.x
          </span>
        </span>
        <span className="text-xs text-muted">
          Pay by 7th of next month · interest 1% if delayed
        </span>
      </header>
      <ol className="p-4 flex flex-col gap-0">
        {events.map((e, idx) => (
          <li
            key={e.dateLabel + e.title}
            className={`grid grid-cols-[110px_minmax(0,1fr)] gap-4 ${
              idx < events.length - 1 ? "pb-4" : ""
            } ${e.muted ? "opacity-60" : ""}`}
          >
            <div className="text-right text-xs uppercase tracking-wider text-muted font-semibold pt-1 border-r-2 border-line pr-3.5">
              {e.dateLabel}
            </div>
            <div className="pl-3.5 relative">
              <span
                className={`absolute -left-[6px] top-[5px] w-3 h-3 rounded-full ring-2 ring-paper ${e.dotClass}`}
                aria-hidden
              />
              <div className="text-sm text-ink-2 font-semibold">{e.title}</div>
              <div className="text-xs text-muted mt-0.5">{e.subtitle}</div>
              <div className="mt-1.5 inline-flex items-center gap-2 flex-wrap">
                <span
                  className={`inline-flex items-center px-1.5 py-px rounded-full text-xs font-bold uppercase tracking-wider border ${e.pillClass}`}
                >
                  {e.pillLabel}
                </span>
                {e.cta ? (
                  <DisabledButton tone="mini" tooltip="Lands in V1.x once challan-cash flow ships">
                    {e.cta}
                  </DisabledButton>
                ) : null}
              </div>
            </div>
          </li>
        ))}
      </ol>
    </section>
  );
}

interface TimelineEvent {
  dateLabel: string;
  title: string;
  subtitle: string;
  pillLabel: string;
  pillClass: string;
  dotClass: string;
  cta?: string | undefined;
  muted?: boolean | undefined;
}

function buildTimelineEvents(data: TdsSummaryData): TimelineEvent[] {
  // One row per quarter-month from `monthlyDeposits` (real per-month TDS
  // totals computed server-side from `vendor_payments.paid_on`). Months
  // with zero deductions render explicitly as ₹0 instead of an
  // ambiguous "— · V1.x" placeholder — the operator can see at a glance
  // that no liability accrued. Challan generation itself is still V1.x.
  const events: TimelineEvent[] = data.monthlyDeposits.map((dep) => {
    const [dueY, dueM, dueD] = dep.dueOn.split("-");
    const [monthY, monthM] = dep.month.split("-");
    const monthNum = Number(monthM);
    const tdsPaise = BigInt(dep.tdsPaise);
    const hasTds = tdsPaise > 0n;
    return {
      dateLabel: `${dueD} ${monthShort(Number(dueM))} ${String(dueY).slice(-2)}`,
      title: `${monthLongFromN(monthNum)} ${monthY} deposit · ${hasTds ? formatRupees(tdsPaise) : "₹0"}`,
      subtitle: hasTds
        ? `${dep.paymentCount} ${dep.paymentCount === 1 ? "deduction" : "deductions"} · pay via ITNS-281 challan`
        : "No TDS deducted in this month",
      pillLabel: hasTds ? "Pending · V1.x" : "Nil",
      pillClass: hasTds
        ? "bg-bg text-muted border-line"
        : "bg-bg text-muted/70 border-line-2",
      dotClass: hasTds ? "bg-paper border-2 border-line-2" : "bg-paper border-2 border-line",
      cta: hasTds ? "Generate & pay" : undefined,
      muted: !hasTds,
    };
  });

  // Quarter-end 26Q filing event.
  const [dueYStr, dueMStr, dueDStr] = data.return26qDueOn.split("-");
  events.push({
    dateLabel: `${dueDStr} ${monthShort(Number(dueMStr))} ${String(dueYStr).slice(-2)}`,
    title: `26Q · Q${data.quarter} FY ${shortFy(data.fy)} quarterly return`,
    subtitle:
      "Three months consolidated · file via TRACES RPU · Form 16A by " +
      data.form16aDueOn.split("-").slice(0).join("-"),
    pillLabel: "Upcoming · V1.x",
    pillClass: "bg-bg text-muted border-line",
    dotClass: "bg-paper border-2 border-line-2",
    muted: true,
  });
  return events;
}

function monthShort(m: number): string {
  return (
    ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"][m - 1] ??
    ""
  );
}

function monthLongFromN(m: number): string {
  return (
    [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ][m - 1] ?? ""
  );
}

// ── Lower-deduction certificates roster ──────────────────────────

/**
 * LDC roster — mirrors `prototype/compliance/tds.html:1108-1141`. Section-197
 * certificates that allow a vendor to deduct at a lower-than-statutory
 * rate. Flint auto-applies the reduced rate at bill posting once this
 * lands. V1.0 has no LDC storage — the roster renders as a single empty
 * row with the chrome intact.
 */
function LdcRoster() {
  return (
    <section
      className="bg-paper border border-line rounded-md shadow-sm"
      aria-label="Lower-deduction certificates"
    >
      <header className="flex items-center justify-between px-4 py-2.5 border-b border-line">
        <span className="text-sm font-semibold text-ink inline-flex items-center gap-2">
          Lower-deduction certificates
          <span className="px-1.5 py-px rounded-sm bg-bg text-xs font-bold uppercase tracking-wider text-muted border border-line">
            V1.x
          </span>
        </span>
        <span className="text-xs text-muted">Section 197 · vendors with reduced TDS rate</span>
      </header>
      <div className="px-5 py-6 bg-bg/30 text-center text-xs text-muted">
        <div className="font-semibold text-ink-2 mb-1">No certificates on file yet.</div>
        <p className="m-0 leading-relaxed">
          When a vendor's section-197 certificate is uploaded, Flint will check its validity at
          every bill posting · auto-apply the reduced rate · alert you 30 days before expiry to
          renew.
        </p>
      </div>
      <footer className="px-4 py-2.5 border-t border-line bg-bg text-xs text-muted">
        Certificate roster + auto-rate-override lands once the LDC vertical ships.
      </footer>
    </section>
  );
}

// ── Disabled-button helper ────────────────────────────────────────

function DisabledButton({
  children,
  tooltip,
  tone,
  shown = true,
  full = false,
}: {
  children: React.ReactNode;
  tooltip: string;
  tone: "primary" | "secondary" | "mini";
  shown?: boolean;
  full?: boolean;
}) {
  if (!shown) return null;
  const sizeAndShape =
    tone === "mini"
      ? "inline-flex items-center gap-1 px-2.5 py-1 rounded-sm text-xs font-medium"
      : "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-xs font-semibold";
  const skin =
    tone === "primary"
      ? "bg-ink text-paper border border-ink"
      : tone === "mini"
        ? "bg-paper text-ink-2 border border-line"
        : "bg-paper text-ink-2 border border-line";
  const states = "disabled:opacity-50 disabled:cursor-not-allowed";
  return (
    <button
      type="button"
      disabled
      title={tooltip}
      className={`${sizeAndShape} ${skin} ${states} ${full ? "w-full justify-center" : ""}`}
    >
      {children}
    </button>
  );
}

// ── tiles + bits ──────────────────────────────────────────────────

type Accent = "indigo" | "emerald" | "amber" | "rose";

const ACCENT: Record<Accent, { stripe: string; iconBg: string; iconText: string }> = {
  indigo: { stripe: "bg-sky-600", iconBg: "bg-sky-50", iconText: "text-sky-600" },
  emerald: { stripe: "bg-emerald-600", iconBg: "bg-emerald-50", iconText: "text-emerald-600" },
  amber: { stripe: "bg-amber-600", iconBg: "bg-amber-50", iconText: "text-amber-700" },
  rose: { stripe: "bg-rose-600", iconBg: "bg-rose-50", iconText: "text-rose-600" },
};

interface KpiTileProps {
  accent: Accent;
  label: string;
  value: string;
  valueUnit?: string;
  meta?: React.ReactNode;
  icon: React.ReactNode;
  muted?: boolean;
}

function KpiTile({ accent, label, value, valueUnit, meta, icon, muted }: KpiTileProps) {
  const a = ACCENT[accent];
  return (
    <div
      className={`relative bg-paper border border-line rounded-md shadow-sm px-4 pt-3.5 pb-3 overflow-hidden transition-all ${muted ? "opacity-70" : "hover:-translate-y-px hover:shadow-md hover:border-line-2"}`}
    >
      <span className={`absolute top-0 left-0 bottom-0 w-[3px] ${a.stripe}`} aria-hidden />
      <div className="flex items-center justify-between gap-2 mb-1.5">
        <span className="text-xs font-semibold uppercase tracking-wider text-muted truncate">
          {label}
        </span>
        <span
          className={`inline-grid place-items-center w-[22px] h-[22px] rounded-sm ${a.iconBg} ${a.iconText}`}
        >
          {icon}
        </span>
      </div>
      <div className="text-2xl font-bold tracking-tight leading-none">
        <span className="num">{value}</span>
        {valueUnit ? (
          <span className="text-xs font-semibold text-muted ml-1">{valueUnit}</span>
        ) : null}
      </div>
      {meta ? (
        <div className="mt-1 text-xs text-muted flex items-center gap-1.5 flex-wrap">{meta}</div>
      ) : null}
    </div>
  );
}

function Sep() {
  return <span className="inline-block w-[3px] h-[3px] rounded-full bg-line-2" aria-hidden />;
}

function DaysToDue({ dueOn }: { dueOn: string }) {
  const days = daysToDue(dueOn);
  if (days < 0)
    return <span className="font-bold text-rose-700">{Math.abs(days)} days overdue</span>;
  if (days === 0) return <span className="font-bold text-rose-700">due today</span>;
  return <span>{days} days left</span>;
}

// ── Section colour palette ───────────────────────────────────────

interface SectionAccent {
  chip: string;
  bar: string;
}
const SECTION_ACCENT = {
  "194C": { chip: "bg-sky-50 text-sky-700", bar: "bg-sky-600" },
  "194J": { chip: "bg-emerald-50 text-emerald-700", bar: "bg-emerald-600" },
  "194Q": { chip: "bg-rose-50 text-rose-700", bar: "bg-rose-500" },
  "194I": { chip: "bg-amber-50 text-amber-700", bar: "bg-amber-500" },
  "194H": { chip: "bg-violet-50 text-violet-700", bar: "bg-violet-500" },
  other: { chip: "bg-slate-100 text-slate-700", bar: "bg-slate-400" },
} as const satisfies Record<string, SectionAccent>;

function sectionAccent(code: string): SectionAccent {
  return (SECTION_ACCENT as Record<string, SectionAccent>)[code] ?? SECTION_ACCENT.other;
}

// ── Quarter helpers ──────────────────────────────────────────────

interface QuarterCell {
  key: string;
  fy: number;
  quarter: 1 | 2 | 3 | 4;
  label: string;
  status: string;
  dotClass: (isOn: boolean) => string;
}

/**
 * Build an 8-quarter strip ending one quarter after `center`. Past
 * quarters are emerald (closed), current is sky (in prep), future is
 * muted. Matches the prototype's "Q1 25-26 · Filed → Q1 26-27 · Compiling"
 * progression.
 */
function buildQuarterStrip(center: QuarterValue): QuarterCell[] {
  const now = currentFyQuarter();
  const cells: QuarterCell[] = [];
  // 7 quarters back, 1 ahead — including the center.
  for (let offset = -6; offset <= 1; offset++) {
    const stepped = addQuarters(center, offset);
    const isFuture = isAfter(stepped, now);
    const isCurrent = stepped.fy === now.fy && stepped.quarter === now.quarter;
    cells.push({
      key: `${stepped.fy}-q${stepped.quarter}`,
      fy: stepped.fy,
      quarter: stepped.quarter,
      label: `Q${stepped.quarter} ${shortFy(stepped.fy)}`,
      // Past quarters labelled "Closed" not "Filed" — Flint V1 doesn't
      // track actual portal-confirmed filing state (lands with V1.x
      // TRACES sync). "Filed" would have been a lie; "Closed" just
      // means the prep window is past.
      status: isFuture ? "Upcoming" : isCurrent ? "In prep" : "Closed",
      dotClass: (isOn: boolean) => {
        if (isOn) return "bg-sky-600";
        if (isFuture) return "bg-line-2";
        if (isCurrent) return "bg-sky-600";
        return "bg-emerald-600";
      },
    });
  }
  return cells;
}

function addQuarters(q: QuarterValue, offset: number): QuarterValue {
  let absoluteQ = q.fy * 4 + (q.quarter - 1) + offset;
  let fy = Math.floor(absoluteQ / 4);
  let quarter = (((absoluteQ % 4) + 4) % 4) + 1;
  // Re-pin if quarter rolled (shouldn't with the math above, defensive)
  return { fy, quarter: quarter as 1 | 2 | 3 | 4 };
}

function isAfter(a: QuarterValue, b: QuarterValue): boolean {
  if (a.fy !== b.fy) return a.fy > b.fy;
  return a.quarter > b.quarter;
}

function quarterLabel(q: QuarterValue): string {
  return `Q${q.quarter} FY ${shortFy(q.fy)}`;
}

function fyLabel(fy: number): string {
  return `FY ${shortFy(fy)}`;
}

function shortFy(fy: number): string {
  return `${String(fy).slice(-2)}-${String((fy + 1) % 100).padStart(2, "0")}`;
}

/** Today's Indian-FY quarter. Indian FY rolls on Apr 1. */
function currentFyQuarter(): QuarterValue {
  const d = new Date();
  const year = d.getFullYear();
  const month = d.getMonth() + 1; // 1-12
  const fy = month >= 4 ? year : year - 1;
  let quarter: 1 | 2 | 3 | 4;
  if (month >= 4 && month <= 6) quarter = 1;
  else if (month >= 7 && month <= 9) quarter = 2;
  else if (month >= 10 && month <= 12) quarter = 3;
  else quarter = 4; // Jan-Mar
  return { fy, quarter };
}

function avatarInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 0 || parts[0] === "") return "–";
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[1]![0]!).toUpperCase();
}

function daysToDue(dueOn: string): number {
  const due = new Date(`${dueOn}T00:00:00Z`);
  const today = new Date();
  const todayUtc = Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate());
  return Math.round((due.getTime() - todayUtc) / (1000 * 60 * 60 * 24));
}

function absBigInt(n: bigint): bigint {
  return n < 0n ? -n : n;
}

function formatRupees(paise: bigint): string {
  const sign = paise < 0n ? "−" : "";
  const rupees = absBigInt(paise) / 100n;
  return `${sign}₹${rupees.toLocaleString("en-IN")}`;
}

function formatRupeesShort(paise: bigint): string {
  const rupees = paise / 100n;
  if (rupees >= 10_000_000n) return `₹${(Number(rupees) / 10_000_000).toFixed(2)}Cr`;
  if (rupees >= 100_000n) return `₹${(Number(rupees) / 100_000).toFixed(2)}L`;
  return `₹${rupees.toLocaleString("en-IN")}`;
}

/**
 * Narrowed shape of the `getQuarterSummary` response, drawn directly from
 * the api-contract Zod inference. Letting TS infer through tRPC keeps
 * this stable when the contract changes.
 */
type TdsSummaryData = tdsContract.GetQuarterSummaryOutput;
